import {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react";

export interface PageTab {
  value: string;
  label: string;
  disabled?: boolean;
  href?: string;
}

interface PageHeaderState {
  title: string | null;
  tabs: PageTab[];
  currentTab: string | null;
  onTabChange: ((value: string) => void) | null;
}

interface PageHeaderContextType extends PageHeaderState {
  setPageHeader: (state: PageHeaderState) => void;
  clearPageHeader: () => void;
}

const EMPTY_STATE: PageHeaderState = {
  title: null,
  tabs: [],
  currentTab: null,
  onTabChange: null,
};

const PageHeaderContext = createContext<PageHeaderContextType | undefined>(
  undefined,
);

export function PageHeaderProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<PageHeaderState>(EMPTY_STATE);

  const setPageHeader = useCallback((next: PageHeaderState) => {
    setState(next);
  }, []);

  const clearPageHeader = useCallback(() => {
    setState(EMPTY_STATE);
  }, []);

  return (
    <PageHeaderContext.Provider
      value={{ ...state, setPageHeader, clearPageHeader }}
    >
      {children}
    </PageHeaderContext.Provider>
  );
}

export function usePageHeaderContext() {
  const ctx = useContext(PageHeaderContext);
  if (!ctx) {
    throw new Error(
      "usePageHeaderContext must be used within a PageHeaderProvider",
    );
  }
  return ctx;
}
